import { memo, ReactElement, useMemo, useCallback } from "react"
import styled from "styled-components"
import Heading from "components/Typography/Heading"
import { Area, AreaChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis, Legend } from "recharts"
import VariationDisplay from "pages/metrics/VariationDisplay"
import { useQueryState } from "context/queryState"
import ApiAccounts from "api/ApiAccounts"
import { useAppSelector } from "reducers/store"
import { ACCOUNT_EVENTS } from "reducers/accounts/actions-types"
import DatePicker from "components/EvtKit/date-picker/DatePicker"
import generateRandomColor from "utils/generateRandomColor"
import Spinner from "components/EvtKit/spinner/Spinner"
import { useDeepMemo } from "utils/useDeepMemo"

export const Title = styled.span`
  display: flex;
  align-items: center;
  padding: 40px 0px 24px 0px;
`

const ChartContainer = styled.div`
  height: 100%;
  width: 100%;
  border-radius: 8px;
  padding: 20px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
`

const HeaderContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  padding: 0px 8px 0px 20px;
  width: 100%;
`

const VariationContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: start;
  gap: 8px;
  padding-left: 36px;
`

const TotalContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 8px;
`

const InnerContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  width: 100%;
  height: 100%;
`

interface EventsChartProps {
  monthFilter: Date | undefined
  setMonthFilter: React.Dispatch<React.SetStateAction<Date | undefined>>
}

const EventsChart = (props: EventsChartProps): ReactElement | null => {
  const { monthFilter, setMonthFilter } = props

  const { account, accountDetails } = useAppSelector((state) => state.accounts)
  const { data, loading } = accountDetails.accountEvents
  const { dailyResults, summary, eventTypeMap } = data

  const searchFilters = {
    date: monthFilter,
    accountId: account?.id
  }

  useQueryState(
    [ACCOUNT_EVENTS, searchFilters],
    async () =>
      new ApiAccounts().getAccountEvents({
        ...searchFilters
      }),
    ACCOUNT_EVENTS
  )

  const valueKeys = useMemo(() => Object.keys(eventTypeMap), [eventTypeMap])

  const colorMap = useDeepMemo(() => {
    const map: Record<string, string> = {}

    valueKeys.forEach((key) => {
      map[key] = generateRandomColor()
    })

    return map
  }, [valueKeys])

  const tooltipFormatter = useCallback(
    (value: number | string, key: string) => {
      const label = eventTypeMap[key] ?? key

      return [value, label] as [number | string, string]
    },
    [eventTypeMap]
  )

  const tooltipLabelFormatter = useCallback((label: string) => `Data: ${label}`, [])

  const handleDateFilterChange = useCallback((date: Date | null) => {
    setMonthFilter(date || undefined)
  }, [])

  return (
    <ChartContainer>
      <HeaderContainer>
        <VariationContainer>
          <Heading variant="h6">Eventos da conta no mês</Heading>
          {!loading && (
            <TotalContainer>
              <Heading variant="h5">{summary.total}</Heading>
              <VariationDisplay value={summary} />
            </TotalContainer>
          )}
          {loading && <Spinner className="secondary" />}
        </VariationContainer>
        <DatePicker
          dateFormat="dd/MM/yyyy"
          onChange={(dates) => handleDateFilterChange(dates as unknown as Date | null)}
          startDate={monthFilter}
          showMonthYearPicker
          placeholderText="Selecione um mês"
          label="Filtrar por mês"
          shrink
          id="date"
        />
      </HeaderContainer>
      <InnerContainer>
        {!loading && (
          <ResponsiveContainer width="100%" minHeight="400px" height="100%">
            <AreaChart width={500} height={600} data={dailyResults} margin={{ top: 16, bottom: 24, right: 4 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="dateValue" />
              <YAxis />
              <Tooltip
                formatter={tooltipFormatter}
                labelFormatter={tooltipLabelFormatter}
                wrapperStyle={{
                  zIndex: 9999
                }}
              />
              <Legend wrapperStyle={{ bottom: 0 }} verticalAlign="bottom" />
              {valueKeys.map((key) => (
                <Area
                  key={key}
                  type="linear"
                  dataKey={key}
                  name={eventTypeMap[key]}
                  stroke={colorMap[key]}
                  fill={colorMap[key]}
                  fillOpacity={0.6}
                />
              ))}
            </AreaChart>
          </ResponsiveContainer>
        )}
        {loading && <Spinner className="secondary size-large" />}
      </InnerContainer>
    </ChartContainer>
  )
}

export default memo(EventsChart)
